import React from "react";
import Card from "components/card";

const SubscriptionInfo = ({ subscription, isLoading, error }) => {
  if (isLoading) {
    return (
      <Card extra="!p-[20px] text-center h-full">
        <div className="animate-pulse flex flex-col gap-4">
          <div className="h-6 bg-navy-700 rounded w-3/4 mx-auto"></div>
          <div className="h-4 bg-navy-700 rounded w-1/2 mx-auto"></div>
          <div className="h-4 bg-navy-700 rounded w-1/2 mx-auto"></div>
          <div className="h-4 bg-navy-700 rounded w-1/2 mx-auto"></div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card extra="!p-[20px] text-center h-full">
        <div className="text-red-500">Error loading subscription data</div>
        <button 
          className="mt-2 bg-blue-500 text-white px-4 py-2 rounded" 
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </Card>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const formatPrice = (amount, currency) => {
    if (!amount) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  };

  const getPlanName = (planId) => {
    if (!planId || planId === "none") return "No active plan";
    
    if (planId.includes("Monthly")) return "Monthly Plan";
    if (planId.includes("Quarterly")) return "Quarterly Plan";
    if (planId.includes("Annually")) return "Annual Plan";
    
    return planId;
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      cancelled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
      pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
      paused: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
      inactive: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
    };

    return (
      <span className={`text-xs font-medium px-2.5 py-0.5 rounded ${statusClasses[status] || statusClasses.inactive}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <Card extra="!p-[20px] h-full">
      <div className="mb-8 w-full">
        <h4 className="text-xl font-bold text-navy-700 dark:text-white">
          Subscription Details
        </h4>
        <p className="mt-2 text-base text-gray-600 dark:text-gray-400">
          Your current subscription information
        </p>
      </div>

      <div className="flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div className="text-lg font-medium dark:text-white">Plan:</div>
          <div className="font-bold text-navy-700 dark:text-white">
            {getPlanName(subscription?.planId) || "No active plan"}
          </div>
        </div>

        <div className="flex justify-between items-center mb-4">
          <div className="text-lg font-medium dark:text-white">Status:</div>
          <div>{getStatusBadge(subscription?.status || "inactive")}</div>
        </div>

        {subscription?.amount && (
          <div className="flex justify-between items-center mb-4">
            <div className="text-lg font-medium dark:text-white">Amount:</div>
            <div className="font-bold text-navy-700 dark:text-white">
              {formatPrice(subscription.amount, subscription.currency)}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <div className="text-lg font-medium dark:text-white">Start Date:</div>
          <div className="text-gray-600 dark:text-white">
            {formatDate(subscription?.startDate)}
          </div>
        </div>

        <div className="flex justify-between items-center mb-4">
          <div className="text-lg font-medium dark:text-white">Renewal Date:</div>
          <div className="text-gray-600 dark:text-white">
            {formatDate(subscription?.endDate)}
          </div>
        </div>
      </div>
      <div className="flex justify-end mt-8">
        <a
          href="https://syntiumalgo.chargebeeportal.com/portal/v2/login?forward=home"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-6 rounded shadow flex items-center gap-2"
        >
          {/* Cancel/close icon */}
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Cancel Subscription
        </a>
      </div>
    </Card>
  );
};

export default SubscriptionInfo;
